import { err, ok } from '@nanovnaweb/shared';
import { TransportError } from './errors.js';
export class WebSerialTransport {
    port;
    opened = false;
    writer = null;
    pumpReader = null;
    pumpRunning = false;
    closeSubscribers = new Set();
    // Each readable() call creates an independent stream; bytes pumped from the
    // underlying port are broadcast to every controller in this set. Canceling
    // one consumer's stream removes only that controller; the underlying port
    // reader is never cancelled by consumers.
    fanoutControllers = new Set();
    constructor(port) {
        this.port = port;
    }
    get isOpen() {
        return this.opened;
    }
    async open(opts) {
        try {
            await this.port.open({ baudRate: opts.baudRate });
            this.writer = this.port.writable.getWriter();
            this.pumpReader = this.port.readable.getReader();
            this.pumpRunning = true;
            this.opened = true;
            void this.pumpLoop();
            return ok(undefined);
        }
        catch (cause) {
            return err(new TransportError('open-failed', 'failed to open serial port', cause));
        }
    }
    async close() {
        this.opened = false;
        this.pumpRunning = false;
        // Close all fan-out streams so consumers observe a terminal done.
        for (const ctrl of this.fanoutControllers) {
            try {
                ctrl.close();
            }
            catch {
                /* already closed */
            }
        }
        this.fanoutControllers.clear();
        // Release port reader (cancel to unblock any pending read on the port).
        if (this.pumpReader) {
            try {
                await this.pumpReader.cancel();
            }
            catch {
                /* ignore */
            }
            try {
                this.pumpReader.releaseLock();
            }
            catch {
                /* ignore */
            }
            this.pumpReader = null;
        }
        try {
            this.writer?.releaseLock();
        }
        catch {
            /* ignore */
        }
        try {
            await this.port.close();
        }
        catch {
            /* ignore */
        }
        for (const cb of this.closeSubscribers)
            cb();
    }
    async write(bytes) {
        if (!this.writer) {
            return err(new TransportError('closed', 'transport not open'));
        }
        try {
            await this.writer.write(bytes);
            return ok(undefined);
        }
        catch (cause) {
            return err(new TransportError('write-failed', 'write failed', cause));
        }
    }
    readable() {
        return new ReadableStream({
            start: (ctrl) => {
                this.fanoutControllers.add(ctrl);
            },
            cancel: () => {
                // Consumer cancelled their stream. Remove from fan-out set but leave
                // the underlying port reader running so other consumers still work.
                // (The controller reference is tracked by identity; we find it by
                // iterating since the `cancel` callback doesn't receive it.)
                for (const ctrl of this.fanoutControllers) {
                    // Dead controller will throw on enqueue; we just prune it lazily
                    // in pumpLoop. Explicit cleanup is not required here, but clearing
                    // errored controllers early reduces per-chunk overhead.
                    try {
                        // Probe liveness: enqueue a zero-length array is not allowed on
                        // ReadableStream, so instead check .desiredSize which is null
                        // when closed/cancelled.
                        if (ctrl.desiredSize === null) {
                            this.fanoutControllers.delete(ctrl);
                        }
                    }
                    catch {
                        this.fanoutControllers.delete(ctrl);
                    }
                }
            },
        });
    }
    onClose(cb) {
        this.closeSubscribers.add(cb);
        return () => {
            this.closeSubscribers.delete(cb);
        };
    }
    async pumpLoop() {
        const reader = this.pumpReader;
        if (!reader)
            return;
        while (this.pumpRunning) {
            let result;
            try {
                result = await reader.read();
            }
            catch {
                this.pumpRunning = false;
                break;
            }
            if (result.done) {
                this.pumpRunning = false;
                break;
            }
            if (!result.value)
                continue;
            // Broadcast to fan-out, pruning any dead controllers.
            const dead = new Set();
            for (const ctrl of this.fanoutControllers) {
                try {
                    ctrl.enqueue(result.value);
                }
                catch {
                    dead.add(ctrl);
                }
            }
            for (const ctrl of dead)
                this.fanoutControllers.delete(ctrl);
        }
        // Pump exit: close all fan-out streams so consumers see terminal done.
        for (const ctrl of this.fanoutControllers) {
            try {
                ctrl.close();
            }
            catch {
                /* already closed */
            }
        }
        this.fanoutControllers.clear();
    }
}
export function isWebSerialSupported() {
    return (typeof globalThis !== 'undefined' &&
        typeof globalThis.navigator?.serial !== 'undefined');
}
export async function requestWebSerialPort() {
    if (!isWebSerialSupported()) {
        return err(new TransportError('not-supported', 'Web Serial API is not available'));
    }
    try {
        const port = await globalThis.navigator.serial.requestPort();
        return ok(new WebSerialTransport(port));
    }
    catch (cause) {
        return err(new TransportError('permission-denied', 'user declined or no port', cause));
    }
}
//# sourceMappingURL=web-serial-transport.js.map